import type { TimetableSession } from "../../lib/api";
import { pushToGoogleAds } from "../../lib/tracking";

type OpenCheckoutOptions = {
  apiUrl?: string;
  primaryColor?: string;
  gtagConversionId?: string;
  gtagConversionLabel?: string;
  onClosed?: () => void;
};

export function openCheckoutOverlay(
  session: TimetableSession,
  opts: OpenCheckoutOptions = {},
): void {
  // Guard against stacked overlays / duplicate Google Ads conversions from a
  // double-click on "Book Now" before the first overlay has mounted.
  if (document.querySelector("baluu-checkout-overlay")) return;

  const overlay = document.createElement("baluu-checkout-overlay");
  overlay.setAttribute("listing-id", session.courseID);
  overlay.setAttribute("business-id", session.course?.schoolId || "");
  if (opts.apiUrl) overlay.setAttribute("api-url", opts.apiUrl);
  if (opts.primaryColor) overlay.setAttribute("primary-color", opts.primaryColor);

  // Term sessions pass the term eventId so checkout selects the term's first session
  overlay.setAttribute(
    "initial-session-id",
    session.isTerm && session.eventId ? session.eventId : session._id,
  );

  document.body.appendChild(overlay);

  const handleClose = () => {
    overlay.parentNode?.removeChild(overlay);
    document.removeEventListener("baluu:checkout-close", handleClose);
    document.removeEventListener("baluu:booking-completed", handleComplete);
    opts.onClosed?.();
  };

  const handleComplete = (event: Event) => {
    const detail = (event as CustomEvent).detail || {};
    // Another baluu-checkout-overlay on the same host page (a different
    // widget instance) can fire this same document-level event — ignore
    // anything that isn't this widget's own listing.
    if (detail.listingId !== session.courseID) return;

    if (opts.gtagConversionId && opts.gtagConversionLabel) {
      pushToGoogleAds(opts.gtagConversionId, opts.gtagConversionLabel, {
        listingId: detail.listingId,
        businessId: detail.businessId,
        purchaseId: detail.purchaseId,
        total: detail.total,
        currency: detail.currency,
      });
    }

    // Host-page event parity with checkout-loader.ts's openCheckout — lets
    // embedding partners' page-level listeners track What's On bookings too.
    document.dispatchEvent(
      new CustomEvent("baluu:timetable-booking-completed", {
        bubbles: true,
        composed: true,
        detail: { listingId: session.courseID, businessId: session.course?.schoolId || "" },
      }),
    );

    setTimeout(handleClose, 3000);
  };

  // CheckoutOverlay dispatches these on document
  document.addEventListener("baluu:checkout-close", handleClose);
  document.addEventListener("baluu:booking-completed", handleComplete);
}
